import { requestServer } from "$lib/servers.svelte";
import { invalidateServerToken } from "$lib/auth.svelte";

export async function getSeries(server: any,
  libraryId: string, sortMethod: string) {
  if (server == undefined) {
    return { Series: [] }
  }
  try {
    const apiPath =
      libraryId == "all"
        ? "api/v1/series"
        : `api/v1/library/${libraryId}/series/progress`
    const { data, status } = await requestServer(
      server,
      'get',
      `${apiPath}?sort=${sortMethod ?? 'added'}`,
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if (error?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    return { Series: [] }
  }
}

const SERIES_GLOBAL_TIMEOUT_MS = 15_000

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  signal?: AbortSignal,
): Promise<T> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }

    const timer = setTimeout(() => {
      reject(new Error('timeout'))
    }, ms)

    const onAbort = () => {
      clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal?.addEventListener('abort', onAbort, { once: true })

    promise.then(
      (value) => {
        clearTimeout(timer)
        signal?.removeEventListener('abort', onAbort)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        signal?.removeEventListener('abort', onAbort)
        reject(err)
      },
    )
  })
}

/**
 * Fan-out series fetch across servers, yielding each server's results as they
 * arrive so the UI can update without waiting on slow/disconnected hosts.
 */
export async function* getSeriesGlobal(
  servers: any,
  libraryId: string,
  sortMethod: string,
  options?: { timeoutMs?: number; signal?: AbortSignal },
): AsyncGenerator<any[], void, unknown> {
  const list = Object.values(servers ?? {}) as any[]
  if (!list.length) return

  const apiPath =
    libraryId == 'all'
      ? 'api/v1/series'
      : `api/v1/library/${libraryId}/series`
  const timeoutMs = options?.timeoutMs ?? SERIES_GLOBAL_TIMEOUT_MS
  const signal = options?.signal

  const fetchOne = async (server: any): Promise<any[]> => {
    try {
      if (signal?.aborted) return []
      const { data, status } = await withTimeout(
        requestServer(
          server,
          'get',
          `${apiPath}?sort=${sortMethod ?? 'added'}`,
          null,
        ),
        timeoutMs,
        signal,
      )
      if (status == 401) {
        invalidateServerToken(server.uid)
        return []
      }
      return (data?.Series ?? []).map((s: any) => ({ ...s, mediaId: server.uid }))
    } catch {
      return []
    }
  }

  const pending = new Set(list.map((server) => fetchOne(server)))

  while (pending.size > 0) {
    if (signal?.aborted) return

    const finished = await Promise.race(
      [...pending].map((p) => p.then((series) => ({ p, series }))),
    )
    pending.delete(finished.p)

    if (finished.series.length > 0) {
      yield finished.series
    }
  }
}

/** Search series on one server (GET /api/v1/series/search?q=…). */
export async function searchSeries(server: any, query: string, limit = 20) {
  const q = query?.trim()
  if (!server || !q) return { Series: [] }
  try {
    const { data, status } = await requestServer(
      server,
      'get',
      `api/v1/series/search?q=${encodeURIComponent(q)}&limit=${limit}`,
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if (error?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return { Series: [] }
    }
    console.log(error)
    return { Series: [] }
  }
}

/** Fan-out search across all user servers; each hit is tagged with mediaId. */
export async function searchSeriesGlobal(servers: any, query: string, limit = 20) {
  const q = query?.trim()
  if (!q) return []
  const res = await Promise.all(
    Object.values(servers ?? {}).map(async (server: any) => {
      try {
        const data = await searchSeries(server, q, limit)
        return (data?.Series ?? []).map((s: any) => ({ ...s, mediaId: server.uid }))
      } catch {
        return []
      }
    })
  )
  return res.flat()
}

export async function getLibraries(server: any) {
  try {
    const { data, status } = await requestServer(
      server,
      'get',
      `api/v1/libraries`,
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if (error?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    return []
  }
}

export async function getGenresTags(server: any) {
  try {
    const { data, status } = await requestServer(
      server,
      'get',
      'api/v1/genres',
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if (error?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    return []
  }
}

export async function createLibrary(
  server: any,
  Name: string,
  Kind: string,
  Path: string,
  Language = '',
) {
  try {
    const { data, status } = await requestServer(
      server,
      'put',
      'api/v1/libraries',
      {
        Library: {
          Name,
          Kind: Kind.toUpperCase(),
          Path,
          language: Language,
        },
      },
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if (error?.response?.status == 401) {
      invalidateServerToken(server.uid)
    }
    throw error
  }
}

export async function deleteLibrary(server: any, libraryId: string) {
  try {
    const { data, status } = await requestServer(
      server,
      'delete',
      `api/v1/library/${libraryId}`,
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if (error?.response?.status == 401) {
      invalidateServerToken(server.uid)
    }
    throw error
  }
}

export async function editLibrary(
  server: any,
  libraryId: string,
  library: {
    Name: string
    Kind: string
    Path: string
    Language?: string
    agentIdentifier?: string
    scannerIdentifier?: string
  },
) {
  try {
    const { data, status } = await requestServer(
      server,
      'post',
      `api/v1/library/${libraryId}`,
      {
        Library: {
          Name: library.Name,
          Kind: library.Kind.toUpperCase(),
          Path: library.Path,
          language: library.Language ?? '',
          ...(library.agentIdentifier
            ? { agentIdentifier: library.agentIdentifier }
            : {}),
          ...(library.scannerIdentifier
            ? { scannerIdentifier: library.scannerIdentifier }
            : {}),
        },
      },
    )
    if (status == 401) throw { response: { status: 401 } }
    if (status == 403) throw new Error('forbidden')
    return data
  } catch (error) {
    if (error?.response?.status == 401) {
      invalidateServerToken(server.uid)
    }
    if (error?.response?.status == 403) {
      throw new Error('forbidden')
    }
    throw error
  }
}

/** ISO 639-1 library source languages. UI uses `unknown` for empty/unset. */
export const LIBRARY_LANGUAGES: { value: string; label: string }[] = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: 'Japanese' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ko', label: 'Korean' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ru', label: 'Russian' },
  { value: 'th', label: 'Thai' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'id', label: 'Indonesian' },
]

/** Source languages that can be selected as translation-eligible (excludes Unknown). */
export const TRANSLATION_SOURCE_LANGUAGES = LIBRARY_LANGUAGES.filter(
  (o) => o.value !== 'unknown',
)

export const DEFAULT_TRANSLATION_ELIGIBLE_LANGUAGES = ['ja', 'zh', 'ko']

/** Normalize API language ("" = unknown) to a select value. */
export function languageToSelect(code: string | null | undefined): string {
  const c = (code ?? '').trim()
  return c === '' ? 'unknown' : c
}

/** Normalize select value back to API language (unknown → ""). */
export function languageFromSelect(value: string | null | undefined): string {
  const v = (value ?? '').trim()
  return !v || v === 'unknown' ? '' : v
}

export function libraryLanguageLabel(code: string | null | undefined): string {
  const select = languageToSelect(code)
  return LIBRARY_LANGUAGES.find((o) => o.value === select)?.label ?? (code || 'Unknown')
}

export async function scanLibraries(server: any) {
  try {
    const { data, status } = await requestServer(
      server,
      'post',
      'api/v1/libraries/scan',
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if (error?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    throw error
  }
}

export async function scanAndRefreshLibraries(server: any) {
  try {
    const { data, status } = await requestServer(
      server,
      'post',
      'api/v1/libraries/scan-and-refresh',
      null
    )
    if (status == 401) throw { response: { status: 401 } }
    return data
  } catch (error) {
    if (error?.response?.status == 401) {
      invalidateServerToken(server.uid)
      return
    }
    console.log(error)
    throw error
  }
}
